/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { CalendarRange } from 'lucide-react';
import {
  MASCOT_LIMITS,
  MONTH_NAMES,
  MONTH_SHORT_NAMES,
  monthInputs,
  monthName,
  monthPressed,
  pressMonth,
  windowInputBounds,
  yearlyHint,
  type WindowInputs,
} from '../lib/desktopMascot';
import { Button, Input } from '../ui';

/**
 * The gallery window: "Always in the gallery", "Only between" two datetime-local times in the browser's time zone, with
 * "This month" and "Next month" filling in the calendar month's boundaries for a monthly rotation, or "Every year in…"
 * the months toggled below it (Jan to Dec), by the calendar in Pacific time, so a season comes back without new dates.
 * A month toggle reads as pressed only while "Every year in…" is chosen (monthPressed, pressMonth). Used by Add an
 * outfit, an outfit's Edit, and Add several outfits (DesktopMascotCard.tsx, BulkOutfitsSection.tsx).
 */
export function WindowFields({ idPrefix, value, now, timeZone, disabled, onChange }: {
  idPrefix: string;
  value: WindowInputs;
  now: number;
  timeZone: string;
  disabled: boolean;
  onChange: (value: WindowInputs) => void;
}) {
  const bounds = windowInputBounds(now);
  const radio = `${idPrefix}-kind`;
  const months = value.months ?? [];
  return (
    <fieldset disabled={disabled} className="ui-fieldset">
      <legend className="ui-field-label">Gallery window</legend>
      <label className="ui-check">
        <input type="radio" name={radio} checked={value.kind === 'always'} onChange={() => onChange({ ...value, kind: 'always' })} />
        <span>Always in the gallery</span>
      </label>
      <div className="ui-actions">
        <label className="ui-check">
          <input type="radio" name={radio} checked={value.kind === 'between'} onChange={() => onChange({ ...value, kind: 'between' })} />
          <span>Only between</span>
        </label>
        <Input
          size="sm"
          type="datetime-local"
          aria-label="Gallery window start"
          className="ui-w-auto"
          value={value.start}
          min={bounds.startMin}
          max={bounds.max}
          onChange={(e) => onChange({ ...value, kind: 'between', start: e.target.value })}
        />
        <span>and</span>
        <Input
          size="sm"
          type="datetime-local"
          aria-label="Gallery window end"
          className="ui-w-auto"
          value={value.end}
          min={bounds.endMin}
          max={bounds.max}
          onChange={(e) => onChange({ ...value, kind: 'between', end: e.target.value })}
        />
      </div>
      <div className="ui-actions">
        <span className="ui-cell-sub">Monthly rotation:</span>
        <Button size="sm" icon={CalendarRange} onClick={() => onChange({ ...value, ...monthInputs(now, 0) })}>This month ({monthName(now, 0)})</Button>
        <Button size="sm" icon={CalendarRange} onClick={() => onChange({ ...value, ...monthInputs(now, 1) })}>Next month ({monthName(now, 1)})</Button>
      </div>
      {value.kind === 'between' && (
        <span className="ui-cell-sub">
          Time zone: {timeZone}. The window ends at least 5 minutes and at most {MASCOT_LIMITS.maxShowUntilDays} days from now; a PC
          that is offline then switches by its own clock.
        </span>
      )}
      <div className="ui-actions">
        <label className="ui-check">
          <input type="radio" name={radio} checked={value.kind === 'yearly'} onChange={() => onChange({ ...value, kind: 'yearly' })} />
          <span>Every year in…</span>
        </label>
        <div role="group" aria-label="Months of the year, in Pacific time" className="ui-month-toggles">
          {MONTH_SHORT_NAMES.map((label, index) => {
            const month = index + 1;
            // Pressed only while "Every year in…" is chosen; the months it keeps meanwhile come back with it.
            const pressed = monthPressed(value, month);
            return (
              <Button
                key={label}
                size="sm"
                aria-pressed={pressed}
                aria-label={MONTH_NAMES[index]}
                onClick={() => onChange(pressMonth(value, month))}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </div>
      {value.kind === 'yearly' && (
        <span className="ui-cell-sub">
          {yearlyHint(months, now)}
          {months.length > 0 && months.length < 12 && ' It comes back every year with no new dates to enter; a PC that is offline when the months end switches by its own clock.'}
        </span>
      )}
    </fieldset>
  );
}
